export function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message || '');
  }

  return String(error || '');
}

export function isAbortLikeMessage(message: string): boolean {
  const normalized = String(message || '').trim().toLowerCase();
  if (!normalized) return false;

  return normalized.includes('aborterror')
    || normalized.includes('the operation was aborted')
    || normalized.includes('signal is aborted without reason')
    || normalized.includes('request was aborted')
    || normalized.includes('signal has been aborted');
}

export function isAbortLikeError(error: unknown): boolean {
  const name = String((error as { name?: unknown } | null | undefined)?.name || '').trim().toLowerCase();
  const code = String((error as { code?: unknown } | null | undefined)?.code || '').trim().toLowerCase();

  return name === 'aborterror'
    || code === 'request_aborted'
    || code === 'tenant_bootstrap_aborted'
    || isAbortLikeMessage(getErrorMessage(error));
}

// HTTP statuses that indicate a TRANSIENT server / gateway condition and are safe
// to retry with backoff: request timeout, too-early/too-many, and 5xx server errors.
// Deterministic 4xx responses (401/403/404/409/422) are intentionally NOT retried.
const TRANSIENT_HTTP_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

export function isTransientHttpStatus(status: number | null | undefined): boolean {
  return TRANSIENT_HTTP_STATUSES.has(Number(status));
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * True when an error thrown at a fetch boundary should be retried as TRANSIENT:
 * - an explicit transient HTTP status (5xx / 429 / 408), or
 * - a non-abort failure with no HTTP status (network drop / request timeout).
 * Aborts are never retried, and deterministic 4xx response errors are surfaced
 * immediately so real authorization/validation failures are never masked.
 */
export function isTransientFetchError(error: unknown): boolean {
  if (isAbortLikeError(error)) return false;
  const status = Number((error as { status?: unknown } | null | undefined)?.status ?? 0);
  if (status !== 0) return isTransientHttpStatus(status);
  return true;
}
